import { isLiveEntitlementStatus, type UserEntitlement } from '@aysar/contracts';

/**
 * The entitlement a new promo grant would **overwrite**, or `null`.
 *
 * ## Why a "grant" needs this at all
 *
 * `UserEntitlementRepository.grantOrExtend` does not refuse a second grant.
 * Under an advisory lock it finds any live row for `(userId, store)` and
 * `UPDATE`s `productKey`, `tier` and `currentPeriodEnd` **over it**. So comping
 * an App Review account that already holds an *open-ended* annual comp replaces
 * it with a thirty-day monthly one — silently, with no undo, and reported to the
 * operator as a success.
 *
 * `EntitlementAlreadyActiveError` exists and reads like the guard against this;
 * it has **no call site**, and its docblock promises a claim nobody built. There
 * is therefore no refusal to catch, and the only place this can be caught is
 * here, before the write, from rows the viewer has already loaded.
 *
 * ## The predicate is the REPOSITORY's, not the viewer's
 *
 * It selects on the status column alone — `store = 'promo'` and a status in
 * `LIVE_ENTITLEMENT_STATUSES` — with no clock in it. That means a row stamped
 * `active` whose period passed months ago is *still* what the next grant
 * overwrites, even though `entitlementStanding` correctly draws it as `ended`
 * and it entitles nobody. Reasoning from the standing here would offer a plain
 * "grant" for a write that is about to overwrite a row, which is precisely what
 * the confirmation exists to prevent. The two functions disagree on purpose, and
 * each one matches the thing it is describing.
 *
 * At most one row can match: a partial unique index over the live statuses makes
 * two live rows for one member and store impossible, which is also what lets the
 * repository take the first row it finds without an ordering.
 */
export function promoReplacementFor(
  entitlements: readonly UserEntitlement[],
): UserEntitlement | null {
  return (
    entitlements.find(
      (entitlement) => entitlement.store === 'promo' && isLiveEntitlementStatus(entitlement.status),
    ) ?? null
  );
}
